import { Component, Input, OnChanges } from '@angular/core';
import { ApiService } from '@core/http';
import { TPointCustomField } from '@project/view-models';
import isNumber from 'lodash/isNumber';
import { getFullUnitName } from 'src/app/core/helpers/get-unit-name';
import { TranslationPipe } from 'src/app/project/features/translate/translation.pipe';
import { CurrenciesService } from 'src/app/project/modules/custom-fields/currencies/currencies.service';
import { getDecimalPlacesValue } from 'src/app/project/modules/custom-fields/custom-field-numbers/utils/getDecimalPlacesValue';
import { ECustomFieldType } from 'src/app/project/modules/custom-fields/custom-field-types-enums';
import { TCustomField } from 'src/app/project/modules/custom-fields/custom-fields.model';
import { CustomFieldsService } from 'src/app/project/modules/custom-fields/custom-fields.service';
import { setFormulaTooltipTitle } from 'src/app/project/modules/custom-fields/utils/create-formula-tooltip';
import { EIconPath } from 'src/app/project/shared/enums/icons.enum';

@Component({
  selector: 'pp-point-fields-formula',
  templateUrl: './point-fields-formula.component.html',
  styleUrl: './point-fields-formula.component.scss',
  standalone: false,
})
export class PointFieldsFormulaComponent implements OnChanges {
  @Input() ppWorkspaceId: string;
  @Input() ppFieldId: string;
  @Input() ppFieldLabel: string;
  @Input() ppField: TPointCustomField;
  @Input() ppFieldCurrencyCode: string;
  @Input() ppFieldCurrencySymbol: string;
  @Input() ppNew: boolean;
  @Input() ppFieldDescription: string;

  value = '';
  EIconPath = EIconPath;
  unitSymbol: string;
  tooltipTitle: string;
  unitSymbolTooltip: string;

  private decimalPlaces: number;
  private showCommas: boolean;
  private customField: TCustomField;

  constructor(
    private customFieldsService: CustomFieldsService,
    private translationPipe: TranslationPipe,
    private apiService: ApiService,
    private currenciesService: CurrenciesService,
  ) {}

  ngOnChanges(): void {
    this.customField =
      this.customFieldsService.getCustomFields()[this.ppWorkspaceId]?.[this.ppFieldId];

    if (this.customField) {
      this.setFormulaDisplayData();
      this.setTooltipTitle();

      this.value = this.formatValue(this.ppField.value);
    }
  }

  private setFormulaDisplayData(): void {
    switch (this.customField.outputType) {
      case ECustomFieldType.PERCENTAGE:
        this.decimalPlaces = 0;
        this.unitSymbol = '%';
        this.showCommas = false;
        this.unitSymbolTooltip = '';
        break;
      case ECustomFieldType.NUMBERS:
        this.unitSymbol = this.customField.unit;
        this.showCommas = this.customField.showCommas;
        this.decimalPlaces = getDecimalPlacesValue(this.customField.decimalPlaces);
        this.unitSymbolTooltip = getFullUnitName(this.customField.unit);

        break;
      case ECustomFieldType.COST:
        this.unitSymbol = this.ppFieldCurrencyCode;
        this.showCommas = this.customField.showCommas;
        const currency = this.currenciesService.getCurrencyByCode(this.ppFieldCurrencyCode);
        this.unitSymbolTooltip = `${currency.currencyDisplayName} (${this.ppFieldCurrencySymbol})`;

        this.decimalPlaces = 2;

        break;
    }
  }

  private formatValue(value: string): string {
    if (this.ppNew) {
      return '';
    }

    if (value == null || isNaN(Number(value))) {
      return this.translationPipe.transform('unable_to_calculate_formula');
    }

    let formattedValue = value;

    if (isNumber(this.decimalPlaces)) {
      formattedValue = Number(value).toFixed(this.decimalPlaces);
    }

    if (this.showCommas) {
      const splitFormattedValue: string[] = formattedValue.toString().split('.');

      if (splitFormattedValue[0].length >= 3) {
        splitFormattedValue[0] = splitFormattedValue[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      }

      formattedValue = splitFormattedValue.join('.');
    }

    return formattedValue;
  }

  private setTooltipTitle(): void {
    setFormulaTooltipTitle(
      this.ppFieldId,
      this.customFieldsService.getCustomFields()[this.ppWorkspaceId],
    ).then((tooltip) => {
      this.tooltipTitle = tooltip;
    });
  }
}
